import { createMuiTheme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { fade } from '@material-ui/core';

import { dateTimePickerThemeOverrides } from './dateTimePickerThemeOverrides';
import accordionOverrides from './accordionOverrides';
import buttonOverrides from './buttonOverrides';
import checkboxOverrides from './checkboxOverrides';
import chipOverrides from './chipOverrides';
import fabOverrides from './fabOverrides';
import formControlLabelOverrides from './formControlLabelOverrides';
import formGroupOverrides from './formGroupOverrides';
import radioOverrides from './radioOverrides';
import tableOverrides from './tableOverrides';
import textFieldOverrides from './textFieldOverrides';
import tooltipOverrides from './tooltipOverrides';
import { darkThemeVariables, lightThemeVariables, zIndicesVariables } from './themeVariables';
import toggleButtonsGroupOverrides from './toggleButtonsGroupOverrides';
import { autocompleteOverrides } from './autocompleteOverrides';

import {
  CheckboxCheckedCustomIcon,
  CheckboxCustomIcon,
  CheckboxIndeterminateCustomIcon,
  DownOneIcon,
  RadioCheckedCustomIcon,
  RadioCustomIcon,
} from '~/icons';

const getMuiThemeOverrides = palette => {
  const { text7, text9 } = palette.primary;
  const shadowColor = fade(palette.background.b1, 0.24);

  return {
    overrides: {
      MuiPopover: {
        paper: {
          borderRadius: 8,
          boxShadow: `0px 8px 24px 0px ${shadowColor}`,
        },
      },
      MuiSelect: {
        icon: {
          top: 'calc(50% - 8px)',
        },
      },
      MuiMenu: {
        paper: {
          borderRadius: 8,
          boxShadow: `0px 8px 24px 0px ${shadowColor}`,
          root: {
            overscrollBehaviorY: 'contain',
            '& .MuiMenuItem-root': {
              backgroundColor: palette.background.b9,
              '&$selected': {
                backgroundColor: palette.primary.text9,
              },
            },
          },
          // backgroundColor: '#ffffff',
          // NOTE: We need this because of bug in native app with dropdowns scrolling.
          // https://developers.google.com/web/updates/2017/11/overscroll-behavior
        },
      },
      MuiMenuItem: {
        root: {
          minHeight: 36,
          color: palette.primary.contrastText,
        },
      },
      MuiTooltip: {
        tooltip: { backgroundColor: palette.background.b9 },
      },
      MuiSwitch: {
        switchBase: { color: text7 },
        track: { backgroundColor: text9, opacity: 1 },
        colorPrimary: { '&$checked': { '& + $track': { opacity: 0.38 } } },
        colorSecondary: { '&$checked': { '& + $track': { opacity: 0.38 } } },
      },
      MuiDialogTitle: {
        root: {
          padding: '40px 40px 16px 40px',
          '@media (max-width: 960px)': {
            padding: '16px',
          },
        },
      },
      MuiDialogContent: {
        root: {
          padding: '0 40px 0 40px',
          '@media (max-width: 960px)': {
            padding: '0 16px',
          },
        },
      },
      MuiDialogContentText: {
        root: {
          color: palette.primary.text6,
          lineHeight: '22.5px',
          marginBottom: 0,
        },
      },
      MuiDialog: {
        paper: {
          backgroundColor: palette.background.b7,
        },
      },
      MuiDialogActions: {
        root: {
          padding: '32px 40px 40px 40px',
          '@media (max-width: 960px)': {
            padding: 16,
          },
        },
        spacing: {
          // NOTE: Increased margin between buttons from 8 to 16
          '&>:not(:first-child)': { marginLeft: 16 },
        },
      },
      MuiListItemIcon: {
        root: {
          minWidth: 48,
        },
      },
      ...accordionOverrides,
      ...buttonOverrides,
      ...checkboxOverrides,
      ...chipOverrides,
      ...dateTimePickerThemeOverrides,
      ...fabOverrides,
      ...formControlLabelOverrides,
      ...formGroupOverrides,
      ...radioOverrides,
      ...tableOverrides,
      ...textFieldOverrides,
      ...tooltipOverrides,
      ...toggleButtonsGroupOverrides,
      ...autocompleteOverrides,
    },
  };
};

// TODO: Delete this when migrating to mui v5
// We override default menu props to address this issue:
// https://github.com/mui-org/material-ui/issues/19245
const defaultProps = {
  MuiFab: { color: 'primary' },
  MuiMenu: { getContentAnchorEl: () => null }, // produces warning
  MuiCheckbox: {
    color: 'primary',
    icon: <CheckboxCustomIcon />,
    checkedIcon: <CheckboxCheckedCustomIcon />,
    indeterminateIcon: <CheckboxIndeterminateCustomIcon />,
  },
  MuiRadio: {
    color: 'primary',
    icon: <RadioCustomIcon />,
    checkedIcon: <RadioCheckedCustomIcon />,
  },
  MuiChip: { deleteIcon: <CloseIcon /> },
  MuiSelect: {
    IconComponent: props => <DownOneIcon theme="filled" fill="#BDBDBD" size={16} {...props} />,
  },
};

export const lightTheme = createMuiTheme({
  props: defaultProps,
  ...zIndicesVariables,
  ...lightThemeVariables,
  ...getMuiThemeOverrides(lightThemeVariables.palette),
  isLightTheme: true,
});

export const darkTheme = createMuiTheme({
  props: defaultProps,
  ...zIndicesVariables,
  ...darkThemeVariables,
  palette: {
    ...darkThemeVariables.palette,
    type: 'dark',
  },
  ...getMuiThemeOverrides(darkThemeVariables.palette),
  isLightTheme: false,
});
